<template>
  <l-map
    class="map"
    :zoom="zoom"
    :center="center"
    :options="mapOptions"
    @update:center="centerUpdate"
    @update:zoom="zoomUpdate"
  >
    <l-tile-layer :url="url"/>
    <l-marker
      v-if="selectedData.length"
      :lat-lng="selectedClusterCoordinates"
      :icon="selectedClusterIcon"
    ></l-marker>
    <l-marker-cluster @clusterclick="clickCluster" @click="clickMarker" :options="clusterOptions">
      <l-marker
        v-for="item in dataWithCoordinates"
        :key="item.id"
        :lat-lng="convertCoordinates(item.coordinates)"
        :data="item"
      ></l-marker>
    </l-marker-cluster>
  </l-map>
</template>

<script>
import { config } from "../config";
import { scaleLog } from "d3-scale";
import { L, LMap, LTileLayer, LMarker } from "vue2-leaflet";
import Vue2LeafletMarkerCluster from "vue2-leaflet-markercluster";

// Fix for wrong icon path in current version of vue2 leaflet library
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: require("leaflet/dist/images/marker-icon-2x.png"),
  iconUrl: require("leaflet/dist/images/marker-icon.png"),
  shadowUrl: require("leaflet/dist/images/marker-shadow.png")
});

export default {
  name: "Map",
  components: {
    LMap,
    LTileLayer,
    LMarker,
    "l-marker-cluster": Vue2LeafletMarkerCluster
  },
  computed: {
    clusterOptions: function() {
      let iconCreateFunction = cluster => {
        let c = "circle-cluster ";
        let childCount = cluster.getChildCount();
        let markers = cluster.getAllChildMarkers();
        let selected = this.getItemsForMarkers(markers);
        let typeCount = this.getTypeCount(selected);
        let hoverpopup = this.getHoverPopup(typeCount, childCount);
        this.scale.domain([1, this.data.length]);
        let size = this.scale(childCount);
        return new L.DivIcon({
          className: c,
          html: hoverpopup,
          iconSize: [size, size]
        });
      };
      return {
        iconCreateFunction,
        maxClusterRadius: 40,
        singleMarkerMode: true,
        spiderfyOnMaxZoom: false,
        showCoverageOnHover: false,
        zoomToBoundsOnClick: false
      }
    },
    dataWithCoordinates: function() {
      return this.data.filter(d => d.coordinates);
    },
    selectedClusterIcon: function() {
      let size = this.scale(this.selectedData.length);
      return new L.DivIcon({
        className: "circle-cluster selected",
        iconSize: [size, size]
      })
    },
  },
  data() {
    return {
      center: L.latLng(47.41322, -1.219482),
      currentCenter: L.latLng(47.41322, -1.219482),
      currentZoom: 11.5,
      mapOptions: {},
      scale: new scaleLog().range([10, 80]),
      selectedClusterCoordinates: false,
      url: "http://{s}.tile.osm.org/{z}/{x}/{y}.png",
      zoom: 5
    };
  },
  methods: {
    centerUpdate(center) {
      this.currentCenter = center;
    },
    getHoverPopup(typeData, childCount) {
      let htmlout = '<div class="popup">';
      let maxShow = config.maxTypesInPopup;
      let types = typeData.map(d => d[0]);
      let label = types.length > 1 ? 'Item' : types[0];
      htmlout += `<div>${childCount} ${label}${childCount > 1 ? 's' : ''}</div>`;
      if (types.length > 1) {
        htmlout += `<ul>`;
        typeData.sort( (a, b) => b[1] - a[1]);
        for (let i = 0; i < typeData.length; i++) {
          htmlout += `<li>${typeData[i][1]} ${typeData[i][0]}</li>`;
          if ( i == maxShow - 1 ) {
            let numOthers = typeData.splice(maxShow - 1).reduce( (accumulator, currentValue) => accumulator + currentValue[1], 0)
            htmlout += `<li>${numOthers} other${numOthers > 1 ? 's' : ''}</li>`
            break;
          }
        }
        htmlout += '</ul>';
      }
      htmlout += '</div>';
      return htmlout;
    },
    getTypeCount(selectedData) {
      let occurances = [];
      let datalen = selectedData.length;
      while (datalen--) {
        occurances.push(selectedData[datalen].type);
      }
      let types = Array.from([...new Set(occurances)]);
      let typelen = types.length;
      while (typelen--) {
        types[typelen] = [types[typelen], 0];
      }
      for (var x = 0; x < occurances.length; x++) {
        for (var y = 0; y < types.length; y++) {
          if (occurances[x] === types[y][0]) {
            types[y][1]++;
          }
        }
      }
      return types;
    },
    clickCluster(e) {
      let markers = e.layer.getAllChildMarkers();
      let selected = this.getItemsForMarkers(markers);
      let coordinates = getUniqueCoordinates(selected.map(d => d.coordinates));
      this.typecount = this.getTypeCount(selected, this.typecount);
      this.selectedClusterCoordinates = e.latlng;
      this.selectData("coordinates", coordinates, true);
    },
    clickMarker(e) {
      this.selectedClusterCoordinates = e.latlng;
      this.selectData("coordinates", [ [e.latlng.lat, e.latlng.lng] ], true);
    },
    convertCoordinates(coordinates) {
      return L.latLng(coordinates[0], coordinates[1]);
    },
    getItemsForMarkers(markers) {
      let allCoordinates = markers.map(
        m => `${m._latlng.lat}, ${m._latlng.lng}`
      );
      let coordinates = [...new Set(allCoordinates)];
      return this.data.filter(
        d =>
          d.coordinates &&
          coordinates.includes(`${d.coordinates[0]}, ${d.coordinates[1]}`)
      );
    },
    selectData(field, conditions, clear) {
      this.$emit("select-data", { field, conditions, clear });
    },
    zoomUpdate(zoom) {
      this.currentZoom = zoom;
    }
  },
  props: {
    data: Array,
    selectedData: Array
  }
};

function getUniqueCoordinates(coordinates) {
  let uniqueCoordinateStrings = [
    ...new Set(coordinates.map(d => `${d[0]}&${d[1]}`))
  ];
  return uniqueCoordinateStrings.map(d => d.split("&"));
}
</script>

<style lang="less">
@import "../../node_modules/leaflet/dist/leaflet.css";
@import "~leaflet.markercluster/dist/MarkerCluster.css";
@import "~leaflet.markercluster/dist/MarkerCluster.Default.css";

.circle-cluster {
  background-color: rgba(255, 120, 20, 0.4);
  border: 3px solid rgba(255, 120, 20, 0.8);
  border-radius: 50%;
  text-align: center;
  &:hover {
    background-color: rgba(255, 120, 20, 0.8);
    border: 3px solid rgba(255, 120, 20, 1);
    z-index: 1000 !important;
    .popup {
      opacity: 1;
    }
  }
  &.selected, &.selected:hover{
    background-color: rgba(41, 92, 128, .9);
    border-color: rgba(41, 92, 128, 1);
    z-index: 900 !important;
  }
  .child-count {
    display: none;
  }
  .popup {
    .transition(all .5s);
    color: rgb(86, 86, 86);
    border-radius: 20px;
    border-top-left-radius: 0;
    background-color: rgba(255, 255, 255, 0.95);
    box-shadow: 10px 10px 50px rgba(0,0,0, .3);
    font-size: 1rem;
    font-weight: bold;
    left: 60%;
    opacity: 0;
    position: relative;
    padding: 8px;
    pointer-events: none;
    top: 60%;
    text-align: left;
    text-shadow: 0 0px 9px rgba(255, 255, 255, 1);
    width: 220px;
    ul {
      font-size: 0.8rem;
      font-weight: normal;
      margin: 0;
      margin-left: 0;
      list-style: none;
      padding: 0;
    }
  }

}

/* */
.transition (@transition) {
  -webkit-transition: @transition;
  -moz-transition: @transition;
  -ms-transition: @transition;
  -o-transition: @transition;
}
</style>
